// The League page's regions a person may move and hide, and how their
// arrangement is read (scenario L3, PLAN-2026-10-02-your-account-follows-you.md
// §6, B-247).
//
// The install offer is not here: it comes and goes on its own. Every section
// of the front page is, in its usual order (B-403).
//
// THE ACCOUNT KEEPS WHAT A CLIENT SENT, so this reads it defensively: an id
// this build does not know is dropped, and a region the person never placed
// — one added after they arranged the page — goes in its usual place, after
// the region that comes before it in the usual order.

import type { LeaguePageLayout } from '@fsp/domain';
import type { DetailLevel } from '@fsp/theme';

export interface PageRegion {
  id: string;
  /** Its panel's title, which is how the person knows it. */
  label: string;
  /** The level it first shows at, or null for every level. */
  from: DetailLevel | null;
}

/**
 * The front page's sections (B-403), in a front page's order. Every one shows
 * at every level: a level takes numbers out of a section, never the section.
 * The old page's regions (Your week, the news, the pulse) went with it; an
 * account that arranged them has its table and awards placed as it chose.
 */
export const LEAGUE_PAGE_REGIONS: readonly PageRegion[] = [
  { id: 'headline', label: "The week's story", from: null },
  { id: 'standings', label: 'Standings', from: null },
  // Every team's character, as a team photo (PLAN-2026-10-24-faces-round-three §4).
  { id: 'photo', label: 'Team photo', from: null },
  // The weekly hat beside it (PLAN-2026-10-30-the-weekly-hat §7).
  { id: 'hat', label: 'The hat', from: null },
  // Pick'em beside the standings (I6, B-447).
  { id: 'pickem', label: 'Pick’em', from: null },
  // The weekly challenge beside it (Q9, PLAN-2026-11-01-the-weekly-challenge §7).
  { id: 'challenge', label: 'Weekly challenge', from: null },
  // Worth a look beside it (E1, PLAN-2026-10-27-worth-a-look §6).
  { id: 'worth', label: 'Worth a look', from: null },
  { id: 'week', label: 'This week', from: null },
  { id: 'wire', label: 'The wire', from: null },
  { id: 'awards', label: 'Awards', from: null },
  { id: 'schedule', label: 'Schedule and playoffs', from: null },
  { id: 'archive', label: 'The archive', from: null },
  { id: 'office', label: 'The office', from: null },
];

const USUAL = LEAGUE_PAGE_REGIONS.map((r) => r.id);

export interface Arrangement {
  /** Every region, in the person's order, hidden ones keeping their place. */
  order: string[];
  hidden: string[];
}

export const USUAL_ARRANGEMENT: Arrangement = { order: USUAL, hidden: [] };

/** The stored layout as this build can draw it. */
export function arrangementOf(layout: LeaguePageLayout | null): Arrangement {
  if (layout === null) return USUAL_ARRANGEMENT;
  const order: string[] = [];
  for (const id of layout.order) if (USUAL.includes(id) && !order.includes(id)) order.push(id);
  for (const [i, id] of USUAL.entries()) {
    if (order.includes(id)) continue;
    const before = USUAL.slice(0, i)
      .reverse()
      .find((b) => order.includes(b));
    order.splice(before === undefined ? 0 : order.indexOf(before) + 1, 0, id);
  }
  const hidden = USUAL.filter((id) => layout.hidden.includes(id));
  return { order, hidden };
}

export function isUsual(a: Arrangement): boolean {
  return a.hidden.length === 0 && a.order.every((id, i) => id === USUAL[i]);
}

/**
 * One region a place up or down among the ones shown, stepping over hidden
 * ones so every tap visibly moves it; the ends stay put.
 */
export function moved(a: Arrangement, id: string, by: -1 | 1): Arrangement {
  const shown = a.order.filter((x) => !a.hidden.includes(x));
  const at = shown.indexOf(id);
  const neighbor = shown[at + by];
  if (at === -1 || neighbor === undefined) return a;
  return by === -1 ? droppedBefore(a, id, neighbor) : droppedBefore(a, neighbor, id);
}

/**
 * One region let go on another, dragging under a pointer: it takes that
 * one's place, so a region dragged down lands after the target and one
 * dragged up lands before it — the way a list one can drag always works,
 * and the only way a drag can reach the last place.
 */
export function droppedOn(a: Arrangement, id: string, target: string): Arrangement {
  const from = a.order.indexOf(id);
  const to = a.order.indexOf(target);
  if (from === -1 || to === -1 || from === to) return a;
  const order = a.order.filter((x) => x !== id);
  order.splice(from < to ? order.indexOf(target) + 1 : order.indexOf(target), 0, id);
  return { ...a, order };
}

/** One region put just before another (the ↑ and ↓ buttons' step). */
export function droppedBefore(a: Arrangement, id: string, target: string): Arrangement {
  if (id === target || !a.order.includes(id) || !a.order.includes(target)) return a;
  const order = a.order.filter((x) => x !== id);
  order.splice(order.indexOf(target), 0, id);
  return { ...a, order };
}

export function withHidden(a: Arrangement, id: string, hide: boolean): Arrangement {
  const hidden = hide ? [...new Set([...a.hidden, id])] : a.hidden.filter((x) => x !== id);
  return { ...a, hidden: USUAL.filter((x) => hidden.includes(x)) };
}

/** The regions on screen, in order: the page's own first, then the person's. */
export function inOrder<T extends { id: string }>(widgets: readonly T[], a: Arrangement): T[] {
  const mine = new Set(USUAL);
  const fixed = widgets.filter((w) => !mine.has(w.id));
  const placed = a.order
    .filter((id) => !a.hidden.includes(id))
    .flatMap((id) => widgets.filter((w) => w.id === id));
  return [...fixed, ...placed];
}
